// Localized storefront display.
import { tr, useStoreLanguage } from '@/i18n';
import { useState, useEffect, useRef } from 'react';
import { shouldUseSupabase } from '@/lib/supabaseClient';
import { getProductVariants } from '@/services/cartService';
import { CartItem } from '@/context/CartContext';
import ProductImage from '@/components/base/ProductImage';

interface ProductVariant {
  id: string;
  label: string;
  price: number;
  stock: number;
  weight_g: number | null;
  position: number;
}

interface CartItemWithVariantsProps {
  item: CartItem;
  onUpdateQuantity: (id: string, quantity: number) => void;
  onRemove: (id: string) => void;
  onChangeVariant: (itemId: string, newVariantId: string, currentQuantity: number) => Promise<void>;
}

const LOCK_TTL_MS = 30 * 60 * 1000; // 30 minutos
const EXPIRY_WARN_MS = 5 * 60 * 1000; // 5 minutos

function usePriceLockCountdown(priceLockedAt: string | null | undefined) {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!priceLockedAt) {
      setSecondsLeft(null);
      return;
    }

    const lockedTime = new Date(priceLockedAt).getTime();

    const calc = () => {
      const remaining = Math.floor((lockedTime + LOCK_TTL_MS - Date.now()) / 1000);
      setSecondsLeft(remaining > 0 ? remaining : 0);
    };

    calc();
    const interval = setInterval(calc, 1000);
    return () => clearInterval(interval);
  }, [priceLockedAt]);

  return secondsLeft;
}

function PriceLockBadge({ priceLockedAt }: { priceLockedAt: string | null | undefined }) {
  useStoreLanguage();
  const secondsLeft = usePriceLockCountdown(priceLockedAt);

  if (!priceLockedAt || secondsLeft === null) return null;

  const minutesLeft = Math.ceil(secondsLeft / 60);
  const isExpiringSoon = secondsLeft > 0 && secondsLeft <= EXPIRY_WARN_MS / 1000;
  const isExpired = secondsLeft === 0;

  if (isExpired) {
    return (
      <div className="inline-flex items-center gap-1 mt-1 bg-gray-100 text-gray-400 text-[10px] font-medium px-1.5 py-0.5 rounded">
        <i className="ri-lock-unlock-line text-[10px]"></i>{tr("Reserva expirada")}</div>
    );
  }

  if (isExpiringSoon) {
    return (
      <div className="inline-flex items-center gap-1 mt-1 bg-amber-50 text-amber-600 text-[10px] font-semibold px-1.5 py-0.5 rounded animate-pulse">
        <i className="ri-alarm-warning-line text-[10px]"></i>{tr("Reserva expira pronto (")}{tr(minutesLeft)}{tr(" min)")}</div>
    );
  }

  return (
    <div className="inline-flex items-center gap-1 mt-1 bg-forest-50 text-forest-600 text-[10px] font-medium px-1.5 py-0.5 rounded">
      <i className="ri-lock-2-line text-[10px]"></i>{tr("Precio reservado (hasta ")}{tr(minutesLeft)}{tr(" min)")}</div>
  );
}

const CartItemWithVariants = ({
  item,
  onUpdateQuantity,
  onRemove,
  onChangeVariant,
}: CartItemWithVariantsProps) => {
  useStoreLanguage();
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [showSelector, setShowSelector] = useState(false);
  const [changingVariant, setChangingVariant] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);

  const isBundle = item.itemType === 'bundle';

  useEffect(() => {
    if (isBundle) return;
    const load = async () => {
      if (!shouldUseSupabase() || !item.productId) return;
      const v = await getProductVariants(item.productId);
      setVariants(v);
    };
    load();
  }, [item.productId, isBundle]);

  useEffect(() => {
    if (!showSelector) return;
    const handler = (e: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(e.target as Node)) {
        setShowSelector(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSelector]);

  const handleSelectVariant = async (variant: ProductVariant) => {
    if (variant.id === item.variantId || variant.stock <= 0) return;
    setChangingVariant(true);
    try {
      await onChangeVariant(item.id, variant.id, item.quantity);
    } finally {
      setChangingVariant(false);
      setShowSelector(false);
    }
  };

  const hasMultipleVariants = variants.length > 1;
  const lineTotal = item.lineTotal ?? (item.unitPrice * item.quantity);

  /* ---- Render para BUNDLE ---- */
  if (isBundle) {
    return (
      <div className="flex gap-4 bg-amber-50/60 rounded-xl p-4 relative border border-amber-200/50">
        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-white">
          {tr(item.image ? (
            <img src={item.image} alt={tr(item.name)} className="w-full h-full object-cover object-top" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-amber-100">
              <i className="ri-gift-line text-2xl text-amber-500"></i>
            </div>
          ))}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
              <i className="ri-gift-line text-[10px]"></i>{tr("Pack")}</span>
          </div>
          <h4 className="font-inter font-medium text-gray-900 text-sm truncate mt-1">{tr(item.name)}</h4>
          <p className="text-forest-600 font-semibold text-sm mt-1">
            {tr(item.unitPrice.toFixed(2).replace('.', ','))} &euro;
          </p>
          <div className="flex items-center gap-2 mt-2">
            <button onClick={() => onUpdateQuantity(item.id, item.quantity - 1)} className="w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded-md hover:bg-gray-100 transition-colors cursor-pointer">
              <i className="ri-subtract-line text-sm"></i>
            </button>
            <span className="w-8 text-center text-sm font-medium">{tr(item.quantity)}</span>
            <button onClick={() => onUpdateQuantity(item.id, item.quantity + 1)} className="w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded-md hover:bg-gray-100 transition-colors cursor-pointer">
              <i className="ri-add-line text-sm"></i>
            </button>
          </div>
        </div>
        <div className="flex flex-col items-end justify-between">
          <button onClick={() => onRemove(item.id)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors cursor-pointer">
            <i className="ri-delete-bin-line text-lg"></i>
          </button>
          {tr(item.quantity > 1 && (
            <span className="text-xs text-gray-500 font-medium whitespace-nowrap">
              {tr(lineTotal.toFixed(2).replace('.', ','))} &euro;
            </span>
          ))}
        </div>
      </div>
    );
  }

  /* ---- Render para PRODUCTO ---- */
  return (
    <div className="flex gap-4 bg-gray-50 rounded-xl p-4 relative">
      <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-white">
        <ProductImage
          src={item.image}
          alt={tr(item.name)}
          productName={item.name}
          className="w-full h-full object-cover object-top"
          fallbackClassName="w-full h-full"
          lazy={false}
        />
      </div>

      <div className="flex-1 min-w-0">
        <h4 className="font-inter font-medium text-gray-900 text-sm truncate">{tr(item.name)}</h4>

        {tr(item.variantLabel && (
          <div className="relative" ref={selectorRef}>
            {tr(hasMultipleVariants ? (
              <button
                onClick={() => setShowSelector(!showSelector)}
                disabled={changingVariant}
                className="inline-flex items-center gap-1 mt-0.5 font-inter text-xs text-forest-600 hover:text-forest-700 bg-forest-50 hover:bg-forest-100 px-2 py-0.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                {tr(changingVariant ? (
                  <>
                    <span className="w-3 h-3 border-2 border-forest-300 border-t-forest-600 rounded-full animate-spin"></span>{tr("Cambiando...")}</>
                ) : (
                  <>
                    {tr(item.variantLabel)}
                    <i className={`ri-arrow-${showSelector ? 'up' : 'down'}-s-line text-xs`}></i>
                  </>
                ))}
              </button>
            ) : (
              <p className="font-inter text-xs text-gray-500 mt-0.5">{tr(item.variantLabel)}</p>
            ))}

            {tr(showSelector && hasMultipleVariants && (
              <div className="absolute top-full left-0 mt-1 z-20 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px] max-w-[220px] animate-fade-in">
                <p className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{tr("Cambiar formato")}</p>
                {tr(variants.map((v) => {
                  const isSelected = v.id === item.variantId;
                  const isOutOfStock = v.stock <= 0;
                  return (
                    <button
                      key={v.id}
                      onClick={() => handleSelectVariant(v)}
                      disabled={isOutOfStock || isSelected}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left transition-colors ${
                        isSelected ? 'bg-forest-50 text-forest-700 cursor-default' : isOutOfStock ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 cursor-pointer'
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {tr(isSelected && (
                          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-line text-forest-600 text-sm"></i>
                          </span>
                        ))}
                        <span className={`font-inter text-xs font-medium truncate ${isSelected ? '' : 'ml-6'}`}>{tr(v.label)}</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className={`font-inter text-xs font-semibold ${isSelected ? 'text-forest-600' : isOutOfStock ? 'text-gray-300' : 'text-gray-900'}`}>
                          {tr(v.price.toFixed(2).replace('.', ','))} &euro;
                        </span>
                        {tr(isOutOfStock && (
                          <span className="text-[9px] font-bold text-red-400 bg-red-50 px-1 py-0.5 rounded leading-none whitespace-nowrap">{tr("Agotado")}</span>
                        ))}
                        {tr(!isOutOfStock && v.stock <= 5 && (
                          <span className="text-[9px] font-bold text-amber-600 bg-amber-50 px-1 py-0.5 rounded leading-none whitespace-nowrap">{tr("Quedan ")}{tr(v.stock)}</span>
                        ))}
                      </div>
                    </button>
                  );
                }))}
              </div>
            ))}
          </div>
        ))}

        {/* Precio unitario con tachado si hay descuento */}
        <div className="flex items-center gap-2 mt-1">
          <p className="text-forest-600 font-semibold text-sm">
            {tr(item.unitPrice.toFixed(2).replace('.', ','))} &euro;
          </p>
          {tr(item.originalUnitPrice != null && item.originalUnitPrice > item.unitPrice && (
            <p className="text-gray-400 text-xs line-through">
              {tr(item.originalUnitPrice.toFixed(2).replace('.', ','))} &euro;
            </p>
          ))}
        </div>

        {/* Badge dinámico de precio reservado */}
        <PriceLockBadge priceLockedAt={item.priceLockedAt} />

        {/* Quantity controls */}
        <div className="flex items-center gap-2 mt-2">
          <button onClick={() => onUpdateQuantity(item.id, item.quantity - 1)} className="w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded-md hover:bg-gray-100 transition-colors cursor-pointer">
            <i className="ri-subtract-line text-sm"></i>
          </button>
          <span className="w-8 text-center text-sm font-medium">{tr(item.quantity)}</span>
          <button onClick={() => onUpdateQuantity(item.id, item.quantity + 1)} className="w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded-md hover:bg-gray-100 transition-colors cursor-pointer">
            <i className="ri-add-line text-sm"></i>
          </button>
        </div>
      </div>

      {/* Columna derecha: eliminar + total de línea */}
      <div className="flex flex-col items-end justify-between">
        <button onClick={() => onRemove(item.id)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors cursor-pointer">
          <i className="ri-delete-bin-line text-lg"></i>
        </button>
        <div className="flex flex-col items-end gap-0.5">
          <span className="text-sm font-semibold text-gray-800 whitespace-nowrap">
            {tr(lineTotal.toFixed(2).replace('.', ','))} &euro;
          </span>
          {tr(item.quantity > 1 && (
            <span className="text-[10px] text-gray-400 whitespace-nowrap">
              {tr(item.quantity)} &times; {tr(item.unitPrice.toFixed(2).replace('.', ','))} &euro;
            </span>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fadeIn 0.15s ease-out; }
      `}</style>
    </div>
  );
};

export default CartItemWithVariants;
